import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, Loader2, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { full_name: '', relationship: '', phone: '', email: '', preferred_method: 'call', scope: 'all', address_ids: [], instructions: '', share_permission: false };
const METHOD = { call: 'Call', text: 'Text', email: 'Email' };
const addrLabel = (a) => [a.line1 || a.street, a.city].filter(Boolean).join(', ') || 'Property';

const validate = (f) => {
  if (f.full_name.trim().length < 2) return 'Enter the contact’s full name';
  if (!f.phone.trim() && !f.email.trim()) return 'Add at least one way to reach this contact';
  if (f.phone.trim() && f.phone.replace(/\D/g, '').length < 10) return 'Enter a valid phone number';
  if (f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return 'Enter a valid email address';
  if (f.preferred_method !== 'email' && !f.phone.trim()) return 'A phone number is needed for the preferred method';
  if (f.preferred_method === 'email' && !f.email.trim()) return 'An email address is needed for the preferred method';
  if (f.scope === 'properties' && !f.address_ids.length) return 'Choose at least one property';
  return null;
};

const ContactForm = ({ initial, properties, onSave, onClose }) => {
  const [f, setF] = useState(initial);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    const e = validate(f); setErr(e); if (e) return;
    setSaving(true);
    try { await onSave(f); } catch (x) { setErr(x.response?.data?.detail || 'Could not save — your entries are kept, please try again.'); } finally { setSaving(false); }
  };
  const toggleAddr = (id) => set('address_ids', f.address_ids.includes(id) ? f.address_ids.filter((x) => x !== id) : [...f.address_ids, id]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle>{initial.id ? 'Edit contact' : 'Add a contact'}</DialogTitle>
          <DialogDescription>Someone the team may call if the primary client can't be reached. They don't get a login, bills, marketing, or the right to approve service or pricing changes.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Full name</Label><Input value={f.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="contact-name" /></div>
          <div><Label className="text-xs">Relationship / role</Label><Input value={f.relationship} onChange={(e) => set('relationship', e.target.value)} placeholder="Partner, property manager…" data-testid="contact-relationship" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => set('phone', e.target.value)} data-testid="contact-phone" /></div>
          <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="contact-email" /></div>
          <div><Label className="text-xs">Preferred contact method</Label>
            <Select value={f.preferred_method} onValueChange={(v) => set('preferred_method', v)}>
              <SelectTrigger data-testid="contact-method"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(METHOD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Applies to</Label>
            <Select value={f.scope} onValueChange={(v) => set('scope', v)}>
              <SelectTrigger data-testid="contact-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All properties</SelectItem><SelectItem value="properties" disabled={!properties.length}>Specific properties</SelectItem></SelectContent>
            </Select></div>
        </div>
        {f.scope === 'properties' && (
          <div className="space-y-1.5" data-testid="contact-property-list">
            {properties.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.address_ids.includes(a.id)} onCheckedChange={() => toggleAddr(a.id)} data-testid={`contact-property-${a.id}`} /> {addrLabel(a)}</label>
            ))}
          </div>
        )}
        <div><Label className="text-xs">Contact instructions</Label><Textarea rows={2} value={f.instructions} onChange={(e) => set('instructions', e.target.value)} placeholder="Best times, what they can help with…" data-testid="contact-instructions" /></div>
        <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.share_permission} onCheckedChange={(v) => set('share_permission', !!v)} data-testid="contact-share" /> <span>OK to share these details with the assigned team for service contact</span></label>
        {err && <p className="text-xs text-red-600" data-testid="contact-form-error">{err}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-[#252a20] hover:bg-black text-white" onClick={submit} disabled={saving} data-testid="contact-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save contact'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Shared Additional Contacts manager. baseUrl: '/api/portal/contacts' or '/api/admin/clients/:id/contacts'
export const AdditionalContacts = ({ baseUrl, headers, properties = [], title = 'Additional contacts' }) => {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => axios.get(`${API_URL}${baseUrl}`, { headers }).then((r) => setList(r.data)).catch(() => setList([])), [baseUrl, headers]);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    const body = { ...f, phone: f.phone.trim(), email: f.email.trim() };
    if (f.id) await axios.put(`${API_URL}${baseUrl}/${f.id}`, body, { headers });
    else await axios.post(`${API_URL}${baseUrl}`, body, { headers });
    toast.success('Contact saved'); setEditing(null); load();
  };
  const move = async (i, d) => {
    const ids = list.map((c) => c.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    try { const r = await axios.post(`${API_URL}${baseUrl}/reorder`, { ids }, { headers }); setList(r.data); } catch { toast.error('Could not reorder'); }
  };
  const remove = async () => {
    try { await axios.delete(`${API_URL}${baseUrl}/${removing.id}`, { headers }); toast.success('Contact removed'); load(); } catch { toast.error('Could not remove the contact'); }
    setRemoving(null);
  };
  const scopeText = (c) => c.scope === 'all' ? 'All properties' : properties.filter((a) => c.address_ids.includes(a.id)).map(addrLabel).join(' · ') || 'Specific properties';

  return (
    <section className="rounded-xl border border-[#e7e4db] bg-[#fffefa] p-5" data-testid="additional-contacts">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">BACKUP CONTACTS</p>
          <h3 className="text-lg font-medium text-[#20211d] tracking-tight">{title}</h3>
          <p className="text-xs text-[#77776e] mt-0.5">The team tries the primary client first, then these contacts in order.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setEditing({ ...EMPTY })} data-testid="contact-add-btn"><Plus className="h-4 w-4 mr-1" /> Add contact</Button>
      </div>
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-[#F56F05] mt-4" /> : !list.length ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-[#77776e]" data-testid="contacts-empty"><Users className="h-4 w-4" /> No additional contacts yet.</div>
      ) : (
        <ol className="mt-4 divide-y divide-[#e7e4db]">
          {list.map((c, i) => (
            <li key={c.id} className="py-3 flex items-start gap-3" data-testid={`contact-row-${c.id}`}>
              <span className="text-xs font-bold text-[#7a7c6d] w-5 pt-0.5">{i + 1}</span>
              <div className="flex-1 min-w-0 text-sm">
                <p className="font-semibold text-[#20211d]">{c.full_name}{c.relationship && <span className="font-normal text-[#77776e]"> · {c.relationship}</span>}</p>
                <p className="text-xs text-[#5f6455]">{[c.phone, c.email].filter(Boolean).join(' · ')} · prefers {METHOD[c.preferred_method]}</p>
                <p className="text-[11px] text-[#8c8e82]">{scopeText(c)}{c.share_permission ? '' : ' · not shared with the team'}</p>
                {c.instructions && <p className="text-[11px] text-[#8c8e82] mt-0.5">{c.instructions}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`contact-up-${c.id}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`contact-down-${c.id}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing({ ...EMPTY, ...c })} aria-label="Edit" data-testid={`contact-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setRemoving(c)} aria-label="Remove" data-testid={`contact-remove-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </li>
          ))}
        </ol>
      )}
      {editing && <ContactForm initial={editing} properties={properties} onSave={save} onClose={() => setEditing(null)} />}
      <AlertDialog open={!!removing} onOpenChange={(v) => !v && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {removing?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>The team will no longer see this person as a backup contact.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="contact-remove-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default AdditionalContacts;
